<template>
  <f7-page name="recommendfriend" class="modern-ui">
    <bc-navbar :showLogo="true" :logoSrc="rafLogo"></bc-navbar>

    <template v-if="!loading">
      <img class="raf-img" :src="headerImageUrl" alt="raf-image">

      <div class="page-container">
        <div class="avatar">
          <img :src="salesPersonImg" alt="sales person photo">
        </div>

        <div class="name accent-fg">{{ salesPersonName }}</div>
        <p class="position">{{ salesPersonTitle }}</p>

        <div class="title-container">
          <h2 class="title">Welcome, {{ customerFirstName }}</h2>
          <p class="subtitle">{{ subtitleText }}</p>
        </div>

        <div v-if="showTip" class="card elevated tip">
          <div class="tip-header">
            <div class="tip-title">How to refer more?</div>
            <div class="badge">TIPS AND TRICKS</div>
          </div>
          <p>The easiest referrals come from people who mentioned buying a car recently — even casually. Think about who
            said that in the last month.</p>
        </div>

        <div v-if="showStats" class="card elevated stats">
          <div class="stat-item">
            <span class="stat-value">{{ referred }}</span>
            <span class="stat-label">Referred</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ purchased }}</span>
            <span class="stat-label">Purchased</span>
          </div>
          <div v-if="showPendingStats" class="stat-item">
            <span class="stat-value">{{ pending }}</span>
            <span class="stat-label">Pending</span>
          </div>
          <div v-if="showEarnedStats" class="stat-item">
            <span class="stat-value">{{ earned }}</span>
            <span class="stat-label">Earned</span>
          </div>
        </div>

        <f7-link v-if="showStats" :href="referralHistoryUrl" class="history-link accent-fg">View History</f7-link>

        <div v-if="showMilestone" class="card elevated progress">
          <div class="progress-header">
            <p class="progress-title">Bonus milestone</p>
            <p class="progress-stage">{{ purchased }}/{{ milestoneThreshold }}</p>
          </div>
          <div class="progress-wrapper">
            <div class="progress-container">
              <div v-for="threshold in progressThresholds" :key="threshold" class="progress-point"
                :class="{ active: progressPercentage >= threshold }">
                <svg v-if="progressPercentage >= threshold" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M4.10397 9.03563L10.6808 2.24053C10.836 2.08018 11.017 2 11.224 2C11.4309 2 11.612 2.08018 11.7672 2.24053C11.9224 2.40089 12 2.59145 12 2.8122C12 3.03296 11.9224 3.22325 11.7672 3.38307L4.64718 10.7595C4.49198 10.9198 4.31091 11 4.10397 11C3.89703 11 3.71596 10.9198 3.56075 10.7595L0.223853 7.3118C0.0686483 7.15145 -0.00585002 6.96116 0.000358164 6.74094C0.00656635 6.52071 0.0875312 6.33016 0.243253 6.16926C0.398975 6.00837 0.58341 5.9282 0.796558 5.92873C1.00971 5.92926 1.19388 6.00944 1.34909 6.16926L4.10397 9.03563Z"
                    fill="currentColor" />
                </svg>
              </div>
              <div class="progress-track"></div>
              <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
            </div>
            <img v-if="progressPercentage === 100" src="~@/assets/raf/progress-success.png"
              alt="progress success image">
          </div>

          <p class="progress-description">{{ milestoneDescription }}</p>
        </div>

        <div class="card info-card">
          <div v-if="isNotCompleted" class="icon">
            <component :is="infoCard.icon" />
          </div>
          <img v-else src="~@/assets/raf/finish.png" alt="complete progress image">
          <p class="title">{{ infoCard.title }}</p>
          <p class="text">{{ infoCard.text }}</p>
        </div>

        <div class="actions">
          <f7-link :href="addReferralLink" class="mui-button w-100 accent">
            {{ ctaText }}
          </f7-link>

          <f7-link class="mui-button w-100 stroked accent" :href="whatFriendsSeeUrl">
            <external-link-icon />
            See what friends see
          </f7-link>
        </div>

      </div>
    </template>

    <div v-if="loading" class="skeleton-content">
      <div class="skeleton raf-img"></div>
      <div class="page-container">
        <div class="skeleton avatar"></div>

        <div class="skeleton name"></div>
        <div class="skeleton position"></div>

        <div class="title-container">
          <h2 class="skeleton title"></h2>
          <p class="skeleton subtitle"></p>
        </div>

        <div class="skeleton card stats">
        </div>

        <div class="skeleton history-link"></div>

        <div class="skeleton card progress"></div>

        <div class="skeleton card info-card">
          <div class="icon"></div>
          <div class="skeleton title"></div>
          <div class="skeleton text"></div>
        </div>

        <div class="actions">
          <div class="skeleton link mui-button w-100">
          </div>

          <div class="skeleton link mui-button w-100 stroked">
          </div>
        </div>
      </div>
    </div>

    <loader :show="loading" />
  </f7-page>
</template>

<script>
import { mapState } from 'vuex'
import ReferralMainComponent from '@components/referral/referral-main.vue'
import Loader from '@components/loader.vue'
import { PAGES } from '@/shared/routes.constants.js'
import { ReferralConditions, ReferralProgramType } from '../../shared/enums/referral.enum'
import GiftIcon from '@components/icons/gift.icon.vue'
import CoinIcon from '@components/icons/coin.icon.vue'
import TrophyIcon from '@components/icons/trophy.icon.vue'
import UsersIcon from '@components/icons/users.icon.vue'
import ExternalLinkIcon from '@components/icons/external-link.icon.vue'
/* eslint-disable */

export default {
  name: "recomend-friend",
  components: {
    ReferralMainComponent,
    Loader,
    'giftIcon': GiftIcon,
    'coinIcon': CoinIcon,
    'trophyIcon': TrophyIcon,
    'usersIcon': UsersIcon,
    ExternalLinkIcon
  },
  computed: {
    ...mapState({
      referralInfo: (state) => state.recommendFriend.mainInfo,
      loading: (state) => state.recommendFriend.mainInfoLoading,
      salesPersonName: (state) => state.common.salesPersonFullName,
      salesPersonImg: state => state.common.salesPersonIcon,
      salesPersonTitle: state => state.common.salesTitle
    }),
    customerFirstName() {
      return this.referralInfo && this.referralInfo.customerFirstName
    },
    progressPercentage() {
      const steps = [0, 25, 75, 100]
      const value = this.referralInfo ? this.referralInfo.milestoneCurrentProgress : 0
      const max = this.referralInfo ? this.referralInfo.milestoneThreshold : 3
      return steps[Math.min(value, max)]
    },
    milestoneThreshold() {
      return this.referralInfo && this.referralInfo.milestoneThreshold || 0
    },
    referred() {
      return this.referralInfo && this.referralInfo.referred || 0
    },
    purchased() {
      return this.referralInfo && this.referralInfo.purchased || 0
    },
    pending() {
      return `$${this.referralInfo && this.referralInfo.awardPendingTotal || 0}`
    },
    earned() {
      return `$${this.referralInfo && this.referralInfo.awardTotal || 0}`
    },
    showStats() {
      return this.referralInfo && this.referralInfo.referred
    },
    showMilestone() {
      return this.referralInfo && this.referralInfo.bonusMilestoneEnabled
    },
    isNotCompleted() {
      return this.condition !== ReferralConditions.milestoneComplete
    },
    subtitleText() {
      return this.getContentByKey('subtitle');
    },
    milestoneDescription() {
      return this.getContentByKey('milestoneDescription');
    },
    infoCard() {
      const content = this.getContentByKey('infoCard')
      return content ? content : {
        icon: null,
        title: null,
        text: null
      };
    },
    ctaText() {
      return this.getContentByKey('ctaText');
    },
    condition() {
      return this.getCondition(this.referralInfo);
    },
    content() {
      if (!this.referralInfo) return null;
      return this.populateContentText(this.referralInfo);
    },
    headerImageUrl() { return this.referralInfo && this.referralInfo.headerImageUrl },
    showPendingStats() {
      return this.referralInfo && this.referralInfo.referralProgramId !== ReferralProgramType.NonMonetary
    },
    showEarnedStats() {
      return this.referralInfo && this.referralInfo.referralProgramId !== ReferralProgramType.NonMonetary && this.condition !== ReferralConditions.referralsExistButNonePurchased
    },
    showTip(){
      return this.referralInfo && this.referralInfo.referralProgramId === ReferralProgramType.NonMonetary
    }
  },
  methods: {
    getContentByKey(key) {
      if (!this.referralInfo) {
        return;
      }
      let referralProgramId = this.referralInfo.referralProgramId
      if (!referralProgramId) {
        console.error('Referral program type is not defined');
        return;
      }
      if (!key) {
        console.error('Prop name is not defined');
        return;
      }
      if (!this.content) {
        console.error('Content dictionary is not defined');
        return;
      }
      const programContent = this.content[referralProgramId];
      if (!programContent) {
        console.error('Content by program not found');
        return;
      }
      const contentSet = programContent[this.condition];
      if (!contentSet) {
        console.error('No content for program condition');
        return;
      }
      const content = contentSet[key];
      if (!content) {
        console.error(`No content by key ${key}`);
        return;
      }
      return content;
    },
    getCondition(referralInfo) {
      if (!referralInfo) {
        return;
      }
      const { purchased, milestoneThreshold, referred, milestoneCurrentProgress } = referralInfo;
      let result;
      if (!referred) {
        // no referrals yet
        result = ReferralConditions.noReferralsYet
      } else if (!purchased) {
        // referrals exist but none purchased
        result = ReferralConditions.referralsExistButNonePurchased
      } else if (milestoneCurrentProgress === milestoneThreshold) {
        // milestone complete — most specific, must come before the range check
        result = ReferralConditions.milestoneComplete
      } else if (milestoneCurrentProgress === milestoneThreshold - 1) {
        // one away from milestone
        result = ReferralConditions.oneAwayFromMilestone
      } else {
        // in progress — also covers milestoneCurrentProgress === 0 with purchases, which happens
        // when the purchase counts toward a different program type than the dealer's active one
        result = ReferralConditions.inProgress
      }
      return result;
    },
    // earned = awardTotal
    // reward - referralAward
    // discount - giveReward
    // bonus - bonusMilestoneAmount
    // pending = awardPendingTotal
    populateContentText(referralInfo) {
      const { awardTotal,
        bonusMilestoneAmount,
        giveReward,
        milestoneThreshold,
        referralAward,
        salespersonName,
      } = referralInfo;
      return {
        [ReferralProgramType.NonMonetary]: {
          [ReferralConditions.noReferralsYet]: {
            subtitle: 'Your first reward is one tap away!',
            infoCard: {
              icon: 'giftIcon',
              title: 'Know someone shopping?',
              text: `Connect them with ${salespersonName}`
            },
            ctaText: 'Add your first referral →'
          },
          [ReferralConditions.referralsExistButNonePurchased]: {
            subtitle: 'Your referrals are warming up!',
            infoCard: {
              icon: 'giftIcon',
              title: 'Know someone shopping?',
              text: `Connect them with ${salespersonName}`
            },
            ctaText: 'Refer one more friend →'
          },
          [ReferralConditions.inProgress]: {
            subtitle: 'Your referrals are warming up!',
            infoCard: {
              icon: 'giftIcon',
              title: 'Know someone shopping?',
              text: `Connect them with ${salespersonName}`
            },
            ctaText: 'Refer one more friend →'
          },
          [ReferralConditions.oneAwayFromMilestone]: null,
          [ReferralConditions.milestoneComplete]: null,
        },
        [ReferralProgramType.ReferralAward]: {
          [ReferralConditions.noReferralsYet]: {
            subtitle: 'Your first reward is one tap away!',
            milestoneDescription: `Earn $${bonusMilestoneAmount} bonus when ${milestoneThreshold} referrals buy`,
            infoCard: {
              icon: 'giftIcon',
              title: 'Know someone shopping?',
              text: `Get $${referralAward} for every referral who buys`
            },
            ctaText: 'Add your first referral →'
          },
          [ReferralConditions.referralsExistButNonePurchased]: {
            subtitle: 'Your referrals are warming up!',
            milestoneDescription: `Earn $${bonusMilestoneAmount} bonus when ${milestoneThreshold} referrals buy`,
            infoCard: {
              icon: 'usersIcon',
              title: `Know someone shopping?`,
              text: `Get $${referralAward} for every referral who buys`
            },
            ctaText: 'Add referral →'
          },
          [ReferralConditions.inProgress]:
          {
            subtitle: `$${awardTotal} Earned!`,
            milestoneDescription: `Earn Nice! You earned $${awardTotal}. Refer one more friend who buys.`,
            infoCard: {
              icon: 'coinIcon',
              title: 'Almost there!',
              text: `Get $${referralAward} for every referral who buys`
            },
            ctaText: 'Refer one more friend →'
          },
          [ReferralConditions.oneAwayFromMilestone]:
          {
            subtitle: `You're on a roll — $${bonusMilestoneAmount} Earned!`,
            milestoneDescription: `Get one more purchase to unlock your $${bonusMilestoneAmount} bonus`,
            infoCard: {
              icon: 'trophyIcon',
              title: 'Almost there!',
              text: `Get $${referralAward} for every referral who buys`
            },
            ctaText: 'Refer one more friend →'
          },
          [ReferralConditions.milestoneComplete]:
          {
            subtitle: `Your $${bonusMilestoneAmount} bonus is on its way!`,
            milestoneDescription: `You earned a $${bonusMilestoneAmount} bonus! We may contact you about your bonus. Keep going!`,
            infoCard: {
              icon: 'giftIcon',
              title: 'Keep it going!',
              text: `Earn $${referralAward} for every referral who buys.`
            },
            ctaText: `Refer more friends — earn $${giveReward} each →`
          },
        },
        [ReferralProgramType.GiveAndGet]: {
          [ReferralConditions.noReferralsYet]:
          {
            subtitle: `Your first reward is one tap away!`,
            milestoneDescription: `Earn $${bonusMilestoneAmount} bonus when ${milestoneThreshold} referrals buy`,
            infoCard: {
              icon: 'giftIcon',
              title: 'Know someone shopping?',
              text: `Give $${giveReward} to friends, earn $${referralAward} for you`
            },
            ctaText: 'Add your first referral →'
          },
          [ReferralConditions.referralsExistButNonePurchased]:
          {
            subtitle: `Your referrals are warming up!`,
            milestoneDescription: `Earn $${bonusMilestoneAmount} bonus when ${milestoneThreshold} referrals buy`,
            infoCard: {
              icon: 'usersIcon',
              title: 'Know someone shopping?',
              text: `Give $${giveReward} to friends, earn $${referralAward} for you`
            },
            ctaText: 'Add referral →'
          },
          [ReferralConditions.inProgress]:
          {
            subtitle: `$${referralAward} Earned!`,
            milestoneDescription: `Earn $${bonusMilestoneAmount} bonus when ${milestoneThreshold} referrals buy`,
            infoCard: {
              icon: 'coinIcon',
              title: `Nice! You earned $${referralAward}!`,
              text: `Give $${giveReward} to friends, earn $${referralAward} for you`
            },
            ctaText: 'Refer one more friend →'
          },
          [ReferralConditions.oneAwayFromMilestone]:
          {
            subtitle: `You're on a roll — $${milestoneThreshold} Earned!`,
            milestoneDescription: `One purchase away from your $${milestoneThreshold} bonus!`,
            infoCard: {
              icon: 'trophyIcon',
              title: 'Almost there!',
              text: `Give $${giveReward} to friends, earn $${referralAward} for you`
            },
            ctaText: 'Refer one more friend →'
          },
          [ReferralConditions.milestoneComplete]:
          {
            subtitle: `Your $${milestoneThreshold} bonus is on its way!`,
            milestoneDescription: `You earned a $${milestoneThreshold} bonus! We may contact you about your bonus. Keep going!`,
            infoCard: {
              icon: 'giftIcon',
              title: 'Keep it going!',
              text: `Keep it going! Give $${giveReward} to friends, earn $${referralAward} for you`
            },
            ctaText: `Refer more friends — earn $${giveReward} each →`
          },
        },
      }
    }
  },
  data() {
    return {
      rafLogo: require('@/assets/raf-logo-lg.png'),
      progressThresholds: [25, 75, 100],
      addReferralLink: PAGES.RecommendAFriendReferral,
      whatFriendsSeeUrl: PAGES.RecommendAFriendWhatFriendsSee,
      referralHistoryUrl: PAGES.RecommendAFriendHistory
    };
  },
}
</script>
<style lang="scss" scoped>
$avatar-size: 84px;
$avatar-border: 4px;
$progress-color: #1CB995;

.raf-img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  height: 100%;
  min-height: 200px;
  max-height: 45vw;
}

.page-container {
  padding-inline: 18px;
  padding-block: 8px 16px;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;

  .avatar {
    position: absolute;
    top: 0;
    width: $avatar-size;
    height: $avatar-size;
    aspect-ratio: 1;
    border-radius: 50%;
    border: $avatar-border solid #fff;
    overflow: clip;
    transform: translateY(-50%);
    background-color: #fff;
    box-shadow: 0px 4px 14px 0px #00000026;
    box-sizing: content-box;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  >.name {
    font-size: 20px;
    font-weight: 700;
    margin-left: calc(#{$avatar-size} + #{$avatar-border} + 16px);
    line-height: 1;
  }

  .position {
    font-size: 14px;
    margin-block: 0;
    margin-left: calc(#{$avatar-size} + #{$avatar-border} + 16px);
  }

  .title-container {
    margin-top: 64px;
    padding-bottom: 16px;
    margin-bottom: auto;

    .title {
      font-size: 28px;
      font-weight: 600;
      margin-inline: auto;
      padding: 0;
      line-height: 1.5;
      text-align: center;
    }

    .subtitle {
      font-size: 14px;
      font-weight: 500;
      margin-inline: auto;
      margin: 0;
      text-align: center;
    }
  }
}

.stats {
  display: flex;

  .stat-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;

    &:not(:last-of-type)::after {
      position: absolute;
      display: block;
      content: '';
      right: 0;
      border-left: 1px solid #5C6C8F;
      height: 80%;
    }

    .stat-value {
      font-size: 20px;
      font-weight: 700;
    }

    .stat-label {
      font-size: 10px;
    }
  }
}

.progress {
  margin-bottom: 8px;

  &-header {
    font-weight: 600;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    p {
      margin: 0;
    }
  }

  &-wrapper {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-block: 8px;

    &>img {
      flex-shrink: 0;
    }
  }

  &-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;

    .progress-point {
      width: 22px;
      height: 22px;
      aspect-ratio: 1;
      border-radius: 50%;
      border: 1px solid #5C6C8F;
      z-index: 2;
      background-color: #fff;
      display: grid;
      place-content: center;

      &.active {
        color: #fff;
        border-color: $progress-color;
        background-color: $progress-color;
      }
    }

    .progress-bar {
      height: 4px;
      background-color: $progress-color;
      width: 25%;
      position: absolute;
      z-index: 1;
      border-radius: 4px;
    }

    .progress-track {
      z-index: 0;
      position: absolute;
      border-top: 1px solid #ccc;
      width: 100%;

      &:has(+ .progress-bar) {
        border-style: dashed;
      }
    }
  }

  &-description {
    text-align: center;
    margin: 0;
    color: #354C78;
    font-weight: 500;
    font-size: 14px;
  }
}

.tip {
  margin-bottom: 8px;
  display: grid;
  align-items: start;
  gap: 8px;

  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &-title {
    font-weight: 700;
    font-size: 16px;
  }

  .badge {
    padding: 5px 12px;
    border-radius: 15px;
    font-weight: 700;
    text-transform: uppercase;
    background-color: #FFEDD6;
    color: #FF8F00;
  }
}

.info-card {
  display: grid;
  grid-template-columns: 42px 1fr;
  align-items: center;
  column-gap: 10px;
  row-gap: 4px;

  &:has(img) {
    grid-template-columns: 64px 1fr;
  }

  p {
    margin: 0;
    line-height: 1;
  }

  .icon {
    width: 42px;
  }

  .icon,
  img {
    grid-row: span 2;
  }

  .title {
    font-size: 16px;
    font-weight: 600;
    align-self: end;
  }

  .text {
    font-weight: 500;
    align-self: start;
  }
}

.actions {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-link {
  font-size: 16px;
  margin-block: 16px 8px;
}

.skeleton-content {
  animation: flickerAnimation 2s infinite;
}

.skeleton {
  $skeleton-bg-color: #e6e6e6;
  background-color: $skeleton-bg-color;


  &:not(.raf-img):not(.card):not(.avatar):not(.mui-button) {
    border-radius: 4px;
  }

  &.raf-img {
    aspect-ratio: 16 / 9;
  }

  &.avatar {
    background-color: $skeleton-bg-color;
  }

  &.name {
    width: 120px;
    height: 16px;
    margin-bottom: 8px;
  }

  &.position {
    width: 64px;
    height: 14px;
  }

  .title-container & {
    &.title {
      width: 100%;
      max-width: 180px;
      height: 24px;
      margin-inline: auto;
      margin-bottom: 8px;
    }

    &.subtitle {
      width: 100%;
      max-width: 120px;
      height: 16px;
      margin-inline: auto;
    }
  }

  &.history-link {
    width: 100%;
    max-width: 120px;
    height: 16px;
    margin-inline: auto;
  }

  &.stats {
    min-height: 64px;
  }

  &.progress {
    min-height: 150px;
  }

  &.info-card {
    background-color: #F4FDFB;

    >.skeleton {
      height: 8px;
      width: 100%;
      border-radius: 4px;
      background-color: #D6F7F0;

      &.title {
        max-width: 120px;
      }

      &.text {
        max-width: 240px;
      }
    }

  }

  .actions & {
    &.mui-button {
      height: 32px;
      position: relative;
      width: 100%;

      &:after {
        display: block;
        content: '';
        position: absolute;
        width: 100%;
        max-width: 92px;
        height: 8px;
        background-color: #fff;
        border-radius: 4px;
      }

      &.stroked {
        background-color: transparent;
        border-color: $skeleton-bg-color;

        &:after {
          background-color: $skeleton-bg-color;
        }
      }
    }
  }
}
</style>


// WEBPACK FOOTER //
// recommend-friend.vue?11297ba8